import './ChartsOp.scss';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Кастомный Tooltip в стиле шаблона
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="chart-tooltip">
            <div className="chart-tooltip__label">{label}</div>
            {payload.map((entry, i) => (
                <div key={i} className="chart-tooltip__item">
                    <span className="chart-tooltip__dot" style={{ background: entry.color }} />
                    <span className="chart-tooltip__name">{entry.name}:</span>
                    <span className="chart-tooltip__value">{entry.value}</span>
                </div>
            ))}
        </div>
    );
};

// Общие настройки осей
const axisStyle = { fontSize: 11, fill: 'var(--text-secondary)' };
const gridStyle = { stroke: 'var(--border-color)', strokeOpacity: 0.5, vertical: false };

export default function ChartsOp() {

    const { t } = useTranslation('statistics');
    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL_NO_RAN);

    const jwtToken = localStorage.getItem('accessToken');
    const navigate = useNavigate();

    const [originalData, setOriginalData] = useState({
        "history-of-balance": {
            "statistic-user-balance-deposit": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-exchange-request": {
                "x-axis": [],
                "y-axis": []
            }
        },
        "daily-turnover-rur": {
            "statistic-exchange-request-success": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-exchange-request-success-by-support": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-exchange-request-conversion": {
                "x-axis": [],
                "y-axis": []
            }
        },
        "payout-statistics": {
            "statistic-payout-assigned": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-payout-completed": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-payout-usdt-earned": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-payout-avg-completion-time": {
                "x-axis": [],
                "y-axis": []
            },
            "statistic-payout-conversion": {
                "x-axis": [],
                "y-axis": []
            }
        }

    });

    useEffect(() => {
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, []);

    useEffect(() => {
        const jwtToken = localStorage.getItem('accessToken');
        const fetchData = async () => {
            try {
                const response = await fetch(`${api_url}api/v3/exchange_request/statistic-operator/`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `JWT ${jwtToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const result = await response.json();

                setOriginalData(result);
            } catch (error) {
                // console.error('Ошибка при получении данных:', error);
            }
        };

        fetchData();
    }, []);

    function transformCategoryData1(categoryData, stats) {
        const transformedData = [];

        // Получаем данные для каждого статистического показателя
        stats.forEach((stat) => {
            const { "x-axis": xAxis, "y-axis": yAxis } = categoryData[stat];

            // Объединяем данные из x-axis и y-axis
            for (let i = 0; i < xAxis.length; i++) {
                const dataPoint = transformedData[i] || { name: xAxis[i] };
                dataPoint[stat] = yAxis[i] || 0;
                transformedData[i] = dataPoint;
            }
        });

        // Возвращаем данные со всеми ключами из stats
        return transformedData;
    }

    function transformCategoryData2(categoryData, stats) {
        const transformedData = [];

        // Получаем данные для каждого статистического показателя
        stats.forEach((stat) => {
            const { "x-axis": xAxis, "y-axis": yAxis } = categoryData[stat];

            // Объединяем данные из x-axis и y-axis
            for (let i = 0; i < xAxis.length; i++) {
                const dataPoint = transformedData[i] || { name: xAxis[i] };
                dataPoint[stat] = yAxis[i] || 0;
                transformedData[i] = dataPoint;
            }
        });

        // Преобразуем в формат с uv, pv и amt
        return transformedData.map((data) => ({
            name: data.name,
            [t('statistics:chartDataKeys.turnover')]: data['statistic-exchange-request-success'] || 0,
            [t('statistics:chartDataKeys.disputes')]: data['statistic-exchange-request-success-by-support'] || 0,
        }));
    }



    // Преобразуем данные для history-of-balance
    const historyOfBalanceData = transformCategoryData1(
        originalData["history-of-balance"],
        [
            "statistic-user-balance-deposit",
            "statistic-exchange-request"
        ]
    ).map((data) => ({
        name: data.name,
        [t('statistics:chartDataKeys.deposits')]: data['statistic-user-balance-deposit'] || 0,
        [t('statistics:chartDataKeys.withdrawals')]: data['statistic-exchange-request'] || 0,
    }));

    // Преобразуем данные для daily-turnover-rur
    const dailyTurnoverRurData = transformCategoryData2(
        originalData["daily-turnover-rur"],
        [
            "statistic-exchange-request-success",
            "statistic-exchange-request-success-by-support",
        ]
    );

    // Извлекаем данные для работы
    const conversionData = originalData["daily-turnover-rur"]["statistic-exchange-request-conversion"];

    // Трансформация данных
    const transformData = (data) => {
        const { "x-axis": xAxis, "y-axis": yAxis } = data;
        return xAxis.map((time, index) => ({
            name: time,
            [t('statistics:chartDataKeys.successPercent')]: (yAxis[index] * 100).toFixed(2),
            [t('statistics:chartDataKeys.cancelPercent')]: (100 - yAxis[index] * 100).toFixed(2),
        }));
    };

    const transformedDataConverse = transformData(conversionData);

    // PayOut statistics transformations
    const payoutRequestsData = transformCategoryData1(
        originalData["payout-statistics"],
        [
            "statistic-payout-assigned",
            "statistic-payout-completed"
        ]
    ).map((data) => ({
        name: data.name,
        [t('statistics:chartDataKeys.assigned')]: data['statistic-payout-assigned'] || 0,
        [t('statistics:chartDataKeys.completed')]: data['statistic-payout-completed'] || 0,
    }));

    const payoutUsdtData = (() => {
        const { "x-axis": xAxis, "y-axis": yAxis } = originalData["payout-statistics"]["statistic-payout-usdt-earned"];
        return xAxis.map((time, index) => ({
            name: time,
            [t('statistics:chartDataKeys.earnedUsdt')]: yAxis[index] || 0,
        }));
    })();

    const payoutEfficiencyData = (() => {
        const { "x-axis": xAxis1, "y-axis": yAxisConversion } = originalData["payout-statistics"]["statistic-payout-conversion"];
        const { "y-axis": yAxisAvgTime } = originalData["payout-statistics"]["statistic-payout-avg-completion-time"];
        return xAxis1.map((time, index) => ({
            name: time,
            [t('statistics:chartDataKeys.conversionPercent')]: ((yAxisConversion[index] || 0) * 100).toFixed(2),
            [t('statistics:chartDataKeys.avgTimeHours')]: yAxisAvgTime[index] || 0,
        }));
    })();

    const chartMargin = { top: 24, right: 24, left: 0, bottom: 0 };

    return (
        <div className={theme === 'light' ? "ChartsOp" : "ChartsOp dark"}>
            <div className="content">
                <div className="charts-grid">
                    {/* 1. История баланса USDT — Area Chart с градиентами */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.balanceHistory')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={historyOfBalanceData} margin={chartMargin} syncId="opCharts">
                                    <defs>
                                        <linearGradient id="gradGreen" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3}/>
                                            <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02}/>
                                        </linearGradient>
                                        <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.deposits')} stroke="#22c55e" strokeWidth={2.5} fill="url(#gradGreen)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.withdrawals')} stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradBlue)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 2. Суточный оборот — Area Chart */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.dailyTurnover')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={dailyTurnoverRurData} margin={chartMargin} syncId="opCharts">
                                    <defs>
                                        <linearGradient id="gradPink" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#ec4899" stopOpacity={0.3}/>
                                            <stop offset="100%" stopColor="#ec4899" stopOpacity={0.02}/>
                                        </linearGradient>
                                        <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3}/>
                                            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.turnover')} stroke="#ec4899" strokeWidth={2.5} fill="url(#gradPink)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.disputes')} stroke="#f59e0b" strokeWidth={2.5} fill="url(#gradOrange)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 3. Конверсия — Rounded Bar Chart */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.conversion')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={transformedDataConverse} margin={chartMargin} syncId="opCharts">
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={t('statistics:chartDataKeys.successPercent')} stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey={t('statistics:chartDataKeys.cancelPercent')} stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 4. PayOut: заявки — Area Chart */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.payoutRequests')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={payoutRequestsData} margin={chartMargin} syncId="opCharts">
                                    <defs>
                                        <linearGradient id="gradBlue2" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25}/>
                                            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02}/>
                                        </linearGradient>
                                        <linearGradient id="gradGreen2" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.25}/>
                                            <stop offset="100%" stopColor="#22c55e" stopOpacity={0.02}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.assigned')} stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradBlue2)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.completed')} stroke="#22c55e" strokeWidth={2.5} fill="url(#gradGreen2)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 5. PayOut: заработано USDT — Rounded Bar */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.payoutEarned')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={payoutUsdtData} margin={chartMargin} syncId="opCharts">
                                    <defs>
                                        <linearGradient id="gradYellow" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#eab308" stopOpacity={0.9}/>
                                            <stop offset="100%" stopColor="#eab308" stopOpacity={0.4}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={t('statistics:chartDataKeys.earnedUsdt')} fill="url(#gradYellow)" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 6. PayOut: конверсия и время — Area Chart */}
                    <div className="chart-card">
                        <h3 className="chart-card__title">{t('statistics:charts.payoutConversion')}</h3>
                        <div className="chart-card__body">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={payoutEfficiencyData} margin={chartMargin} syncId="opCharts">
                                    <defs>
                                        <linearGradient id="gradPink2" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#ec4899" stopOpacity={0.25}/>
                                            <stop offset="100%" stopColor="#ec4899" stopOpacity={0.02}/>
                                        </linearGradient>
                                        <linearGradient id="gradOrange2" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25}/>
                                            <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid {...gridStyle} />
                                    <XAxis dataKey="name" tick={axisStyle} tickLine={false} axisLine={false} />
                                    <YAxis tick={axisStyle} tickLine={false} axisLine={false} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.conversionPercent')} stroke="#ec4899" strokeWidth={2.5} fill="url(#gradPink2)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                    <Area type="monotone" dataKey={t('statistics:chartDataKeys.avgTimeHours')} stroke="#f59e0b" strokeWidth={2.5} fill="url(#gradOrange2)" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
